import React from "react";
import { motion } from "framer-motion";

export default function WomenEmpowerment() {
  const empowermentData = {
    title: "Women Empowerment",
    sections: [
      {
        heading: "An Introduction",
        description: `The term women empowerment is all about authority, or the power embarked on women sharing indistinguishable rights. It refers to the liberation of women from socio-economic restraints of reliance. Women comprise around 50% of the country’s population, with a large portion economically dependent and often without employment. In the age of feminism, while some women in India exercise autonomy, a significant number still require support. Many in rural and semi-urban areas are denied basic education and are not authorized to pursue higher education, despite their potential.`,
      },
      {
        heading: "Importance of Women Empowerment",
        description: `In recent times, empowering women has become essential. Women deserve the liberty, confidence, and self-worth to choose their needs and desires. Gender discrimination is obsolete, as shown by the achievements of women over the past decades. Women empowerment in India is necessary to give women an equal role in society. This includes providing access to higher education, equal pay, and respect in various sectors such as economics, politics, and education, to ensure they are treated with the same rights as men.`,
        link: {
          url: "http://ncw.nic.in/",
          text: "National Commission for Women",
        },
      },
    ],
    keyPoints: [
      "Equal rights in all areas of life",
      "Access to higher education and employment",
      "End to gender-based discrimination",
      "Recognition of women's contributions to society",
      "Encouragement for women to pursue their dreams",
    ],
    externalLinks: [
      { title: "SEHORE DISTRICT", url: "https://sehore.nic.in/en/" },
      { title: "MPSWC", url: "https://mpswc.nic.in/" },
      { title: "MPSWCMIS", url: "https://www.mpwcdmis.gov.in/" },
      { title: "WCD", url: "https://wcd.nic.in/hi" },
      { title: "MP ONLINE", url: "https://www.mponline.gov.in/portal/" },
      { title: "MAWE", url: "https://www.maweindia.com/" },
    ],
  };

  return (
    <div className="container mx-auto bg-gray-100 p-10">
      <div className="flex flex-col lg:flex-row gap-10">
        {/* Main Content */}
        <motion.div
          className="lg:w-2/3 bg-white p-2 rounded-lg shadow-lg border-4 border-teal-500"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <motion.h1
            className="text-3xl font-bold mb-6 px-2 py-2 rounded-sm text-white bg-teal-500"
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            {empowermentData.title}
          </motion.h1>

          {/* Render sections */}
          {empowermentData.sections.map((section, index) => (
            <motion.div
              key={index}
              className="mb-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: index * 0.3, duration: 0.8 }}
            >
              <h2 className="text-2xl p-2 font-bold text-teal-500">{section.heading}</h2>
              <p className="text-gray-700 leading-relaxed p-2">{section.description}</p>
              {section.link && (
                <div className="mt-4">
                  <a
                    href={section.link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-teal-600 font-medium hover:underline p-2"
                  >
                    {section.link.text}
                  </a>
                </div>
              )}
            </motion.div>
          ))}

          {/* Related Links Section */}
          <motion.div
            className="mt-6 px-2 mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: empowermentData.sections.length * 0.3, duration: 1 }}
          >
            <h2 className="text-2xl font-bold text-teal-500 mb-4">Related Links</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {empowermentData.externalLinks.map((link, index) => (
                <motion.div
                  key={index}
                  className="p-2 bg-gray-100 rounded-lg shadow hover:shadow-md transition-shadow"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.2, duration: 0.6 }}
                >
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lg font-semibold text-teal-600 hover:underline"
                  >
                    {link.title}
                  </a>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>

        {/* Sidebar - Key Points */}
        <motion.div
          className="lg:w-1/3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <motion.div
            className="bg-teal-500 text-white p-6 rounded-lg shadow-lg border-4 border-white"
            initial={{ x: -100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-2xl font-semibold mb-4">Key Points</h2>
            <ul className="space-y-4 text-white text-lg">
              {empowermentData.keyPoints.map((point, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <span className="w-2 h-2 mt-2 bg-white rounded-full"></span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
